import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Modal } from "components/modal/Modal";
import {
  Tooltip,
  TooltipArrow,
  TooltipContent,
  TooltipTrigger,
} from "components/ui/tooltip";
import { FolderPlusIcon } from "lucide-react";
import { ProjectType } from "reedy-data/models";
import {
  BULK_BOOKS_LIMIT,
  SAVE_AS_PROJECT_BOOKS_WARNING_LIMIT,
} from "shared/constants";
import { SearchProductWithScore } from "hooks/search/types";
import { useProjects } from "pages/Projects/contexts/ProjectsContext";
import { useLocation, useNavigate } from "@tanstack/react-router";
import { Text } from "components/text";
import { newProjectNavigation } from "~/pages/Projects/newProjectNavigation";
import { DropdownMenuItem } from "../../../components/ui/dropdown-menu";

interface CreateProjectDropdownItemProps {
  isSelectedAll?: boolean;
  selectedBooksCount: number;
  selectedIds: string[];
  allBooksCount: number;
  fetchAllResults: () => Promise<SearchProductWithScore[]>;
}

export const CreateProjectDropdownItem = ({
  isSelectedAll,
  selectedBooksCount,
  selectedIds,
  allBooksCount,
  fetchAllResults,
}: CreateProjectDropdownItemProps) => {
  const { createProject } = useProjects();
  const { t } = useTranslation(["books", "general"]);
  const [isWarningOpen, setIsWarningOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  const isLimitReached = selectedBooksCount > BULK_BOOKS_LIMIT;
  const isWarningReached =
    selectedBooksCount > SAVE_AS_PROJECT_BOOKS_WARNING_LIMIT && !isLimitReached;

  const handleSaveProject = async () => {
    let ids = selectedIds;

    if (isSelectedAll && selectedIds.length < allBooksCount) {
      setIsLoading(true);
      const products = await fetchAllResults();
      ids = products.map((p) => p.id);
    }

    const project = await createProject(
      t("general:newProjectName"),
      ids,
      ProjectType.ISBNS,
    );

    setIsWarningOpen(false);
    setIsLoading(false);

    navigate(newProjectNavigation(project!, location.href));
  };

  return (
    <>
      <Modal
        id="save-project"
        loading={isLoading}
        open={isWarningOpen}
        variant="warning"
        title={t("books:selectedBooks.saveProjectWarningModal.header")}
        description={t(
          "books:selectedBooks.saveProjectWarningModal.description",
          { count: SAVE_AS_PROJECT_BOOKS_WARNING_LIMIT },
        )}
        confirmLabel={t("books:selectedBooks.saveAsProject")}
        confirmIconName="loader"
        cancelLabel={t("general:buttons.cancel")}
        onOpenChange={() => setIsWarningOpen(false)}
        onConfirm={handleSaveProject}
      />
      <Tooltip delayDuration={100}>
        <TooltipTrigger asChild>
          <span>
            <DropdownMenuItem
              className="py-2.5 px-3 cursor-pointer"
              disabled={isLimitReached}
              onClick={
                isWarningReached
                  ? (event) => {
                      event.preventDefault();
                      setIsWarningOpen(true);
                    }
                  : handleSaveProject
              }
              role="menuitem"
            >
              <Text as="span" variant="body" className="flex items-center">
                <FolderPlusIcon className="mr-2 h-4 w-4" />
                {t("books:dropdown.takeAction.item.createProject")}
              </Text>
            </DropdownMenuItem>
          </span>
        </TooltipTrigger>
        {isLimitReached ? (
          <TooltipContent
            side="bottom"
            className="font-light text-center shadow-tooltip text-secondary-600 whitespace-pre px-5 z-10 border-none"
            sideOffset={3}
            align="center"
          >
            <TooltipArrow
              className="fill-white drop-shadow-xs"
              width={12}
              height={6}
            />
            <p className="whitespace-pre">
              {t("books:selectedBooks.limit", {
                count: BULK_BOOKS_LIMIT,
              })}
            </p>
          </TooltipContent>
        ) : null}
      </Tooltip>
    </>
  );
};
